import { T, TS } from './constants.js';

// === BIOME DECORATION SYSTEM ===
// Small decorative elements that add atmosphere without heavy performance cost
export class BiomeDecorator {
    constructor(seed) {
        this.seed = seed;
        this.cache = new Map(); // Cache generated decorations per chunk
    }
    
    // Generate small decorative elements for a specific screen region
    // Called only when camera moves to new chunks
    generateForRegion(world, startX, endX, biome) {
        const key = `${Math.floor(startX / 32)}_${biome}`;
        if (this.cache.has(key)) return this.cache.get(key);
        
        const decorations = [];
        const spacing = this._getSpacing(biome);
        
        for (let x = startX; x < endX; x += spacing) {
            const hash = ((x * 7919 + biome * 31337) >>> 0) % 100;
            if (hash < this._getChance(biome)) {
                const deco = this._createDecoration(world, x, biome, hash);
                if (deco) decorations.push(deco);
            }
        }
        
        this.cache.set(key, decorations);
        if (this.cache.size > 50) { // Limit cache size
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }
        
        return decorations;
    }
    
    _getSpacing(biome) {
        // Different biomes have different decoration density
        const dense = [3, 4, 7, 8, 10, 11, 12, 15, 16, 22, 23, 25, 27, 28, 30]; // Dense decoration
        const sparse = [5, 6, 13, 17, 20, 26]; // Sparse decoration
        if (dense.includes(biome)) return 4;
        if (sparse.includes(biome)) return 10;
        return 6;
    }
    
    _getChance(biome) {
        // Percentage chance to spawn decoration at each check point
        const high = [3, 4, 7, 8, 11, 15, 16, 23, 27, 28, 29, 30]; // Lots of decorations
        const medium = [0, 1, 2, 9, 10, 12, 14, 22, 24, 25]; // Medium density
        if (high.includes(biome)) return 75;
        if (medium.includes(biome)) return 55;
        return 40;
    }
    
    _createDecoration(world, x, biome, hash) {
        const sy = Math.floor(world.heightMap[x]);
        const tile = world.gt(x, sy);
        if (world.gt(x, sy - 1) !== T.AIR) return null;
        
        const type = this._getDecoType(biome, hash);
        if (!type) return null;
        
        return { x, y: sy - 1, type, biome, offset: hash % 10, phase: hash * 0.1 };
    }
    
    _getDecoType(biome, hash) {
        const h = hash % 100;
        switch(biome) {
            case 0: case 18: // Tundra/Frozen Shore - ice shards, snow piles, frost sparkle
                if (h < 25) return 'ice_shard';
                if (h < 40) return 'snow_pile';
                if (h < 55) return 'frost_sparkle';
                break;
            case 1: // Taiga - pine cones, animal tracks, frost moss
                if (h < 30) return 'pine_cone';
                if (h < 50) return 'animal_tracks';
                if (h < 65) return 'frost_moss';
                break;
            case 2: // Plains - dandelion puffs, small rocks, grass tuft
                if (h < 25) return 'dandelion';
                if (h < 45) return 'small_rock';
                if (h < 60) return 'grass_tuft';
                break;
            case 3: // Forest - fallen acorn, moss patch, mushroom cluster
                if (h < 25) return 'fallen_acorn';
                if (h < 45) return 'moss_patch';
                if (h < 60) return 'mushroom_cluster';
                break;
            case 4: // Cherry Grove - sakura petals on ground, pink sparkle
                if (h < 40) return 'sakura_petals';
                if (h < 60) return 'pink_sparkle';
                break;
            case 5: case 6: // Desert/Red Desert - tumbleweed, scorpion burrow, heat shimmer
                if (h < 25) return 'tumbleweed';
                if (h < 40) return 'scorpion_burrow';
                if (h < 55) return 'heat_shimmer';
                break;
            case 7: // Jungle - glow flowers, hanging vines, exotic bird
                if (h < 30) return 'glow_flower';
                if (h < 50) return 'hanging_vine';
                if (h < 65) return 'exotic_bird';
                break;
            case 8: // Swamp - lily pads, swamp gas, mosquitos
                if (h < 30) return 'lily_pad';
                if (h < 50) return 'swamp_gas';
                if (h < 65) return 'mosquito';
                break;
            case 9: // Beach - seashell deco, starfish, footprints, seagull
                if (h < 25) return 'seashell_deco';
                if (h < 40) return 'starfish';
                if (h < 55) return 'sand_footprint';
                if (h < 65) return 'seagull';
                break;
            case 10: // Corruption - dark tendrils, shadow eye, corruption spore
                if (h < 30) return 'dark_tendril';
                if (h < 50) return 'shadow_eye';
                if (h < 60) return 'corruption_spore';
                break;
            case 11: // Hallow - sparkles, rainbow dust, fairy light
                if (h < 35) return 'holy_sparkle';
                if (h < 55) return 'rainbow_dust';
                if (h < 65) return 'fairy_light';
                break;
            case 12: // Mushroom Fields - tiny mushrooms, spore puffs, biolum dot
                if (h < 30) return 'tiny_mushroom';
                if (h < 50) return 'spore_cloud';
                if (h < 65) return 'biolum_dot';
                break;
            case 13: case 21: // Volcanic/Ember - ash puffs, embers, lava crack
                if (h < 25) return 'ash_puff';
                if (h < 45) return 'ground_ember';
                if (h < 60) return 'lava_crack';
                break;
            case 14: // Savanna - dry grass tuft, termite trail, bone fragment
                if (h < 30) return 'dry_grass';
                if (h < 50) return 'termite_trail';
                if (h < 60) return 'bone_fragment';
                break;
            case 15: // Dark Forest - glowing eyes, shadow, dark mushroom
                if (h < 30) return 'glowing_eyes';
                if (h < 50) return 'dark_shadow';
                if (h < 65) return 'dark_mushroom';
                break;
            case 16: // Flower Meadow - butterflies, bees, pollen
                if (h < 30) return 'butterfly';
                if (h < 50) return 'bee';
                if (h < 65) return 'pollen_cloud';
                break;
            case 17: // Badlands - cracked earth, dust devil, canyon arch silhouette
                if (h < 30) return 'cracked_earth';
                if (h < 50) return 'dust_devil';
                break;
            case 19: // Crystal - floating crystal shards
                if (h < 40) return 'float_crystal';
                if (h < 55) return 'crystal_sparkle';
                break;
            case 20: // Abyss - bioluminescent spots
                if (h < 35) return 'bio_spot';
                if (h < 50) return 'deep_bubble';
                break;
            case 22: // Overgrown Ruins - vine drip, ancient glyph
                if (h < 35) return 'vine_drip';
                if (h < 55) return 'ancient_glyph';
                break;
            case 23: // Blood Plains - blood puddle, bone pile, eye stalk
                if (h < 30) return 'blood_puddle';
                if (h < 50) return 'bone_fragment';
                if (h < 65) return 'eye_stalk';
                break;
            case 24: // Shimmer Glades - shimmer mote, crystal flower
                if (h < 40) return 'shimmer_mote';
                if (h < 60) return 'crystal_flower';
                break;
            case 25: // Fungal - spore clouds
                if (h < 35) return 'spore_cloud';
                if (h < 55) return 'tiny_mushroom';
                break;
            case 26: // Petrified Wastes - fossil fragment, time crack, petrified root
                if (h < 30) return 'fossil_fragment';
                if (h < 50) return 'time_crack_deco';
                if (h < 60) return 'petrified_root';
                break;
            case 27: // Prism - rainbow wisps
                if (h < 50) return 'prism_wisp';
                break;
            case 28: // Tidal Flats - tide pools, crabs, seaweed, driftwood
                if (h < 20) return 'tide_pool';
                if (h < 35) return 'hermit_crab';
                if (h < 50) return 'seaweed_tuft';
                if (h < 60) return 'driftwood_piece';
                if (h < 70) return 'tide_foam';
                break;
            case 29: // Storm Peaks - static charges, wind, storm debris
                if (h < 20) return 'static_arc';
                if (h < 35) return 'wind_streak';
                if (h < 50) return 'storm_crack';
                if (h < 60) return 'charged_rock';
                if (h < 70) return 'wind_vortex';
                break;
            case 30: // Living Caves - pulsing polyps, veins, eyes
                if (h < 20) return 'flesh_polyp';
                if (h < 35) return 'blood_puddle';
                if (h < 50) return 'nerve_cluster';
                if (h < 60) return 'pulsing_eye';
                if (h < 70) return 'organic_tendril';
                break;
        }
        return null;
    }
    
    // Render decorations in world space (canvas is already transformed)
    render(ctx, decorations, camX, camY, time) {
        for (const d of decorations) {
            const worldX = d.x * TS + TS / 2;
            const worldY = d.y * TS + TS / 2;
            
            // Cull off-screen (approximate check in world coords)
            if (Math.abs(worldX - camX) > 500 || Math.abs(worldY - camY) > 400) continue;
            
            this._renderDecoration(ctx, d, worldX, worldY, time);
        }
    }
    
    _renderDecoration(ctx, d, sx, sy, time) {
        const phase = time / 1000 + d.phase;
        
        switch(d.type) {
            case 'ice_shard':
                ctx.fillStyle = `rgba(180,220,255,${0.6 + Math.sin(phase) * 0.2})`;
                ctx.beginPath();
                ctx.moveTo(sx, sy);
                ctx.lineTo(sx - 3, sy - 8);
                ctx.lineTo(sx + 3, sy - 8);
                ctx.fill();
                break;
                
            case 'snow_pile':
                ctx.fillStyle = 'rgba(240,245,255,0.4)';
                ctx.fillRect(sx - 4, sy, 8, 3);
                ctx.fillRect(sx - 3, sy - 2, 6, 2);
                break;
                
            case 'glow_flower':
                const glow = Math.sin(phase * 2) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(120,255,180,${glow * 0.6})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 3, 3, 0, Math.PI * 2);
                ctx.fill();
                // Glow effect
                ctx.fillStyle = `rgba(120,255,180,${glow * 0.2})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 3, 6, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'hanging_vine':
                const sway = Math.sin(phase) * 2;
                ctx.strokeStyle = 'rgba(60,140,60,0.5)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(sx, sy - 10);
                ctx.lineTo(sx + sway, sy);
                ctx.stroke();
                break;
                
            case 'lily_pad':
                ctx.fillStyle = 'rgba(80,160,80,0.6)';
                ctx.beginPath();
                ctx.ellipse(sx, sy, 6, 4, 0, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'swamp_gas':
                const bubble = Math.sin(phase * 3);
                if (bubble > 0.7) {
                    ctx.fillStyle = `rgba(180,200,100,${(bubble - 0.7) * 2})`;
                    ctx.beginPath();
                    ctx.arc(sx, sy - 5 - bubble * 10, 3, 0, Math.PI * 2);
                    ctx.fill();
                }
                break;
                
            case 'dark_tendril':
                const pulse = Math.sin(phase * 1.5) * 0.3 + 0.5;
                ctx.strokeStyle = `rgba(100,30,140,${pulse})`;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(sx, sy);
                ctx.quadraticCurveTo(sx + 4, sy - 5, sx + 2, sy - 10);
                ctx.stroke();
                break;
                
            case 'holy_sparkle':
                const sparkle = Math.sin(phase * 4) * 0.5 + 0.5;
                if (sparkle > 0.7) {
                    ctx.fillStyle = `rgba(220,220,255,${sparkle})`;
                    ctx.fillRect(sx - 1, sy - 6, 2, 2);
                    ctx.fillRect(sx - 3, sy - 7, 1, 1);
                    ctx.fillRect(sx + 2, sy - 7, 1, 1);
                }
                break;
                
            case 'ash_puff':
                const puff = Math.sin(phase * 2) * 0.4 + 0.3;
                ctx.fillStyle = `rgba(100,100,100,${puff})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 3, 4, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'ground_ember':
                const emberGlow = Math.sin(phase * 3) * 0.5 + 0.5;
                ctx.fillStyle = `rgba(255,${100 + emberGlow * 80 | 0},30,${emberGlow * 0.7})`;
                ctx.fillRect(sx - 2, sy - 2, 4, 4);
                ctx.fillStyle = `rgba(255,200,100,${emberGlow * 0.3})`;
                ctx.fillRect(sx - 4, sy - 4, 8, 8);
                break;
                
            case 'butterfly':
                const flap = Math.sin(phase * 8);
                const bx = sx + Math.sin(phase) * 5;
                const by = sy - 5 + Math.sin(phase * 1.5) * 3;
                ctx.fillStyle = 'rgba(255,180,200,0.7)';
                ctx.fillRect(bx - 3, by, 3 + flap, 2);
                ctx.fillRect(bx + 1, by, 3 - flap, 2);
                break;
                
            case 'bee':
                const buzz = Math.sin(phase * 15);
                const beex = sx + buzz * 2;
                ctx.fillStyle = 'rgba(255,200,50,0.8)';
                ctx.fillRect(beex - 2, sy - 3, 4, 3);
                ctx.fillStyle = 'rgba(100,100,100,0.6)';
                ctx.fillRect(beex - 1, sy - 3, 1, 3);
                break;
                
            case 'float_crystal':
                const bob = Math.sin(phase) * 4;
                const cGlow = Math.sin(phase * 2) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(160,180,255,${cGlow * 0.5})`;
                ctx.beginPath();
                ctx.moveTo(sx, sy - 15 + bob);
                ctx.lineTo(sx - 4, sy - 8 + bob);
                ctx.lineTo(sx + 4, sy - 8 + bob);
                ctx.fill();
                ctx.fillStyle = `rgba(200,220,255,${cGlow * 0.3})`;
                ctx.fillRect(sx - 2, sy - 13 + bob, 4, 3);
                break;
                
            case 'bio_spot':
                const bGlow = Math.sin(phase * 1.5) * 0.4 + 0.6;
                ctx.fillStyle = `rgba(80,200,220,${bGlow * 0.5})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 2, 3, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = `rgba(120,240,255,${bGlow * 0.2})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 2, 6, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'spore_cloud':
                const sPulse = Math.sin(phase) * 0.3 + 0.4;
                ctx.fillStyle = `rgba(170,60,200,${sPulse})`;
                ctx.beginPath();
                ctx.arc(sx, sy - 8, 5 + sPulse * 3, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'prism_wisp':
                const offset = phase % (Math.PI * 2);
                const pr = 128 + Math.sin(offset) * 80 | 0;
                const pg = 128 + Math.sin(offset + 2) * 80 | 0;
                const pb = 128 + Math.sin(offset + 4) * 80 | 0;
                const wispY = sy - 10 + Math.sin(phase * 2) * 5;
                ctx.fillStyle = `rgba(${pr},${pg},${pb},0.6)`;
                ctx.beginPath();
                ctx.arc(sx, wispY, 4, 0, Math.PI * 2);
                ctx.fill();
                break;
                
            case 'tide_pool':
                ctx.fillStyle = 'rgba(80,140,180,0.4)';
                ctx.beginPath();
                ctx.ellipse(sx, sy, 8, 4, 0, 0, Math.PI * 2);
                ctx.fill();
                // Reflection
                const ripple = Math.sin(phase * 2) * 0.2 + 0.3;
                ctx.fillStyle = `rgba(150,200,230,${ripple})`;
                ctx.fillRect(sx - 4, sy - 1, 8, 1);
                break;
                
            case 'hermit_crab':
                const crawl = Math.sin(phase * 0.5) * 3;
                ctx.fillStyle = 'rgba(200,150,100,0.7)';
                ctx.fillRect(sx + crawl - 3, sy - 2, 6, 3);
                ctx.fillStyle = 'rgba(180,120,80,0.7)';
                ctx.fillRect(sx + crawl - 4, sy - 3, 2, 1);
                ctx.fillRect(sx + crawl + 2, sy - 3, 2, 1);
                break;
                
            case 'static_arc':
                if (Math.sin(phase * 5) > 0.8) {
                    ctx.strokeStyle = 'rgba(180,220,255,0.8)';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(sx - 4, sy - 2);
                    ctx.lineTo(sx + 4, sy - 6);
                    ctx.stroke();
                }
                break;
                
            case 'flesh_polyp': {
                const fPulse = Math.sin(phase * 1.8) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(180,60,70,${fPulse * 0.6})`;
                ctx.beginPath();
                ctx.ellipse(sx, sy - 3, 4, 6 * fPulse, 0, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = `rgba(220,100,110,${fPulse * 0.5})`;
                ctx.fillRect(sx - 2, sy - 7, 4, 2);
                break;
            }
            // === NEW DECORATIONS ===
            case 'frost_sparkle': {
                const fs = Math.sin(phase * 3) * 0.5 + 0.5;
                if (fs > 0.6) {
                    ctx.fillStyle = `rgba(200,230,255,${fs})`;
                    ctx.fillRect(sx - 1, sy - 4, 2, 2);
                    ctx.fillRect(sx + 2, sy - 6, 1, 1);
                }
                break;
            }
            case 'pine_cone': {
                ctx.fillStyle = 'rgba(120,80,40,0.7)';
                ctx.fillRect(sx - 2, sy - 3, 4, 4);
                ctx.fillStyle = 'rgba(100,70,35,0.6)';
                ctx.fillRect(sx - 1, sy - 4, 2, 1);
                break;
            }
            case 'animal_tracks': {
                ctx.fillStyle = 'rgba(160,140,120,0.3)';
                ctx.fillRect(sx - 3, sy, 2, 1);
                ctx.fillRect(sx + 1, sy - 1, 2, 1);
                ctx.fillRect(sx + 5, sy, 2, 1);
                break;
            }
            case 'frost_moss': {
                ctx.fillStyle = 'rgba(140,180,160,0.4)';
                ctx.fillRect(sx - 4, sy, 8, 2);
                ctx.fillStyle = 'rgba(180,220,200,0.3)';
                ctx.fillRect(sx - 3, sy - 1, 6, 1);
                break;
            }
            case 'dandelion': {
                const puff = Math.sin(phase * 0.5);
                ctx.strokeStyle = 'rgba(100,140,60,0.5)';
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx, sy - 8); ctx.stroke();
                ctx.fillStyle = `rgba(255,255,240,${0.5 + puff * 0.2})`;
                ctx.beginPath(); ctx.arc(sx, sy - 9, 3 + puff, 0, Math.PI * 2); ctx.fill();
                // Flying seeds
                if (puff > 0.5) {
                    ctx.fillStyle = 'rgba(255,255,240,0.3)';
                    ctx.fillRect(sx + 5 + puff * 3, sy - 12 + puff * 2, 2, 2);
                }
                break;
            }
            case 'small_rock': {
                ctx.fillStyle = 'rgba(140,140,130,0.5)';
                ctx.fillRect(sx - 3, sy - 1, 6, 3);
                ctx.fillStyle = 'rgba(160,160,150,0.4)';
                ctx.fillRect(sx - 2, sy - 2, 4, 1);
                break;
            }
            case 'grass_tuft': {
                const sway2 = Math.sin(phase * 0.8) * 1;
                ctx.strokeStyle = 'rgba(90,160,50,0.5)';
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(sx - 2, sy); ctx.lineTo(sx - 3 + sway2, sy - 6); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx + sway2, sy - 7); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(sx + 2, sy); ctx.lineTo(sx + 3 + sway2, sy - 5); ctx.stroke();
                break;
            }
            case 'fallen_acorn': {
                ctx.fillStyle = 'rgba(140,100,50,0.6)';
                ctx.beginPath(); ctx.arc(sx, sy - 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = 'rgba(100,80,40,0.5)';
                ctx.fillRect(sx - 2, sy - 4, 4, 2);
                break;
            }
            case 'moss_patch': {
                ctx.fillStyle = 'rgba(60,120,40,0.4)';
                ctx.fillRect(sx - 5, sy, 10, 2);
                ctx.fillStyle = 'rgba(80,140,50,0.3)';
                ctx.fillRect(sx - 3, sy - 1, 6, 1);
                break;
            }
            case 'mushroom_cluster': {
                ctx.fillStyle = 'rgba(180,60,60,0.6)';
                ctx.beginPath(); ctx.arc(sx - 2, sy - 3, 3, Math.PI, 0); ctx.fill();
                ctx.beginPath(); ctx.arc(sx + 3, sy - 2, 2, Math.PI, 0); ctx.fill();
                ctx.fillStyle = 'rgba(200,180,160,0.5)';
                ctx.fillRect(sx - 2, sy - 3, 1, 3);
                ctx.fillRect(sx + 3, sy - 2, 1, 2);
                break;
            }
            case 'sakura_petals': {
                ctx.fillStyle = 'rgba(255,180,200,0.5)';
                ctx.fillRect(sx - 3, sy, 2, 1);
                ctx.fillRect(sx + 1, sy - 1, 2, 1);
                ctx.fillRect(sx - 1, sy + 1, 2, 1);
                break;
            }
            case 'pink_sparkle': {
                const ps = Math.sin(phase * 3) * 0.5 + 0.5;
                if (ps > 0.5) {
                    ctx.fillStyle = `rgba(255,180,220,${ps})`;
                    ctx.fillRect(sx, sy - 5, 2, 2);
                    ctx.fillRect(sx - 2, sy - 4, 1, 1);
                }
                break;
            }
            case 'tumbleweed': {
                const roll = phase * 2;
                const tx = sx + Math.sin(roll) * 8;
                ctx.strokeStyle = 'rgba(160,140,80,0.5)';
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.arc(tx, sy - 4, 4, 0, Math.PI * 2); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(tx - 3, sy - 4); ctx.lineTo(tx + 3, sy - 4); ctx.stroke();
                break;
            }
            case 'scorpion_burrow': {
                ctx.fillStyle = 'rgba(100,80,50,0.4)';
                ctx.beginPath(); ctx.arc(sx, sy, 4, Math.PI, 0); ctx.fill();
                ctx.fillStyle = 'rgba(80,60,30,0.5)';
                ctx.fillRect(sx - 3, sy - 1, 6, 1);
                break;
            }
            case 'heat_shimmer': {
                const shimmer2 = Math.sin(phase * 4) * 0.3 + 0.1;
                if (shimmer2 > 0.2) {
                    ctx.fillStyle = `rgba(255,240,200,${shimmer2 * 0.3})`;
                    ctx.fillRect(sx - 6, sy - 3, 12, 2);
                }
                break;
            }
            case 'exotic_bird': {
                const birdY = sy - 10 + Math.sin(phase * 1.5) * 4;
                const birdX = sx + Math.sin(phase * 0.5) * 6;
                const wingFlap = Math.sin(phase * 6) * 0.4;
                ctx.fillStyle = 'rgba(255,80,50,0.6)';
                ctx.fillRect(birdX - 1, birdY, 3, 2);
                ctx.fillRect(birdX - 3, birdY - wingFlap * 3, 2, 1);
                ctx.fillRect(birdX + 2, birdY + wingFlap * 3, 2, 1);
                break;
            }
            case 'mosquito': {
                const mBuzz = Math.sin(phase * 12) * 2;
                const mDrift = Math.sin(phase * 0.8) * 5;
                ctx.fillStyle = 'rgba(60,60,60,0.5)';
                ctx.fillRect(sx + mDrift, sy - 6 + mBuzz, 2, 2);
                break;
            }
            case 'seashell_deco': {
                ctx.fillStyle = 'rgba(240,220,200,0.6)';
                ctx.beginPath(); ctx.arc(sx, sy - 1, 3, Math.PI, 0); ctx.fill();
                ctx.fillStyle = 'rgba(255,200,180,0.4)';
                ctx.fillRect(sx - 2, sy - 1, 4, 1);
                break;
            }
            case 'starfish': {
                ctx.fillStyle = 'rgba(255,140,80,0.6)';
                for (let i = 0; i < 5; i++) {
                    const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
                    ctx.fillRect(sx + Math.cos(a) * 3, sy - 2 + Math.sin(a) * 3, 2, 2);
                }
                ctx.fillRect(sx - 1, sy - 3, 2, 2);
                break;
            }
            case 'sand_footprint': {
                ctx.fillStyle = 'rgba(180,160,120,0.25)';
                ctx.fillRect(sx - 2, sy, 3, 1);
                ctx.fillRect(sx + 2, sy - 2, 3, 1);
                break;
            }
            case 'seagull': {
                const gullY = sy - 20 + Math.sin(phase * 1.2) * 5;
                const gullX = sx + Math.sin(phase * 0.4) * 10;
                const wing = Math.sin(phase * 4) * 2;
                ctx.fillStyle = 'rgba(240,240,240,0.6)';
                ctx.fillRect(gullX - 1, gullY, 3, 2);
                ctx.fillRect(gullX - 4, gullY - wing, 3, 1);
                ctx.fillRect(gullX + 2, gullY + wing, 3, 1);
                break;
            }
            case 'shadow_eye': {
                const eyeOpen = Math.sin(phase * 1.5) * 0.5 + 0.5;
                if (eyeOpen > 0.3) {
                    ctx.fillStyle = `rgba(180,50,200,${eyeOpen * 0.5})`;
                    ctx.beginPath(); ctx.ellipse(sx, sy - 4, 3, 2 * eyeOpen, 0, 0, Math.PI * 2); ctx.fill();
                    ctx.fillStyle = `rgba(255,50,50,${eyeOpen * 0.7})`;
                    ctx.fillRect(sx - 1, sy - 5, 2, 2);
                }
                break;
            }
            case 'corruption_spore': {
                const sp = Math.sin(phase * 2) * 0.4 + 0.4;
                ctx.fillStyle = `rgba(100,20,140,${sp})`;
                ctx.beginPath(); ctx.arc(sx, sy - 6, 2, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = `rgba(80,10,120,${sp * 0.5})`;
                ctx.beginPath(); ctx.arc(sx, sy - 6, 4, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'rainbow_dust': {
                const rd = phase % (Math.PI * 2);
                const rr = 200 + Math.sin(rd) * 55 | 0;
                const rg = 200 + Math.sin(rd + 2) * 55 | 0;
                const rb = 200 + Math.sin(rd + 4) * 55 | 0;
                ctx.fillStyle = `rgba(${rr},${rg},${rb},0.3)`;
                ctx.fillRect(sx - 3, sy - 2, 6, 3);
                break;
            }
            case 'fairy_light': {
                const fl2 = Math.sin(phase * 3) * 0.4 + 0.6;
                const flX = sx + Math.sin(phase) * 4;
                const flY = sy - 8 + Math.sin(phase * 1.5) * 3;
                ctx.fillStyle = `rgba(220,200,255,${fl2 * 0.6})`;
                ctx.beginPath(); ctx.arc(flX, flY, 2, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = `rgba(200,180,255,${fl2 * 0.3})`;
                ctx.beginPath(); ctx.arc(flX, flY, 4, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'tiny_mushroom': {
                ctx.fillStyle = 'rgba(200,60,80,0.6)';
                ctx.beginPath(); ctx.arc(sx, sy - 3, 2.5, Math.PI, 0); ctx.fill();
                ctx.fillStyle = 'rgba(220,200,180,0.5)';
                ctx.fillRect(sx - 0.5, sy - 3, 1, 3);
                break;
            }
            case 'biolum_dot': {
                const bl2 = Math.sin(phase * 2) * 0.4 + 0.6;
                ctx.fillStyle = `rgba(80,255,120,${bl2 * 0.5})`;
                ctx.beginPath(); ctx.arc(sx, sy - 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = `rgba(80,255,120,${bl2 * 0.2})`;
                ctx.beginPath(); ctx.arc(sx, sy - 2, 5, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'lava_crack': {
                const lc2 = Math.sin(phase * 2) * 0.3 + 0.5;
                ctx.strokeStyle = `rgba(255,100,20,${lc2})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(sx - 4, sy);
                ctx.lineTo(sx - 1, sy - 2);
                ctx.lineTo(sx + 2, sy + 1);
                ctx.lineTo(sx + 5, sy - 1);
                ctx.stroke();
                ctx.fillStyle = `rgba(255,60,0,${lc2 * 0.3})`;
                ctx.fillRect(sx - 4, sy - 2, 9, 3);
                break;
            }
            case 'dry_grass': {
                const sway3 = Math.sin(phase * 0.6) * 1;
                ctx.strokeStyle = 'rgba(180,160,80,0.5)';
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(sx - 2, sy); ctx.lineTo(sx - 2 + sway3, sy - 5); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(sx + 1, sy); ctx.lineTo(sx + 1 + sway3, sy - 6); ctx.stroke();
                break;
            }
            case 'termite_trail': {
                ctx.fillStyle = 'rgba(120,100,60,0.3)';
                for (let i = 0; i < 5; i++) {
                    ctx.fillRect(sx + i * 3 - 6, sy + (i % 2), 2, 1);
                }
                break;
            }
            case 'bone_fragment': {
                ctx.fillStyle = 'rgba(220,210,190,0.5)';
                ctx.fillRect(sx - 3, sy - 1, 5, 2);
                ctx.fillRect(sx - 2, sy - 2, 1, 1);
                ctx.fillRect(sx + 1, sy - 2, 1, 1);
                break;
            }
            case 'glowing_eyes': {
                const blink2 = Math.sin(phase * 1.2);
                if (blink2 > -0.3) {
                    const ea = Math.min(0.7, blink2 * 0.5 + 0.4);
                    ctx.fillStyle = `rgba(200,255,50,${ea})`;
                    ctx.fillRect(sx - 3, sy - 5, 2, 2);
                    ctx.fillRect(sx + 1, sy - 5, 2, 2);
                    ctx.fillStyle = `rgba(200,255,50,${ea * 0.3})`;
                    ctx.fillRect(sx - 5, sy - 7, 10, 6);
                }
                break;
            }
            case 'dark_shadow': {
                const ds = Math.sin(phase) * 0.2 + 0.3;
                ctx.fillStyle = `rgba(10,20,10,${ds})`;
                ctx.beginPath(); ctx.ellipse(sx, sy - 2, 8, 4, 0, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'dark_mushroom': {
                ctx.fillStyle = 'rgba(40,30,60,0.6)';
                ctx.beginPath(); ctx.arc(sx, sy - 3, 3, Math.PI, 0); ctx.fill();
                ctx.fillStyle = 'rgba(60,50,40,0.5)';
                ctx.fillRect(sx - 0.5, sy - 3, 1, 3);
                const glp = Math.sin(phase * 2) * 0.3 + 0.3;
                ctx.fillStyle = `rgba(100,255,150,${glp})`;
                ctx.fillRect(sx - 1, sy - 4, 1, 1);
                ctx.fillRect(sx + 1, sy - 3, 1, 1);
                break;
            }
            case 'pollen_cloud': {
                const pc2 = Math.sin(phase) * 0.3 + 0.4;
                ctx.fillStyle = `rgba(255,240,100,${pc2 * 0.4})`;
                ctx.beginPath(); ctx.arc(sx, sy - 6, 4, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'cracked_earth': {
                ctx.strokeStyle = 'rgba(100,80,50,0.4)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(sx - 5, sy - 1);
                ctx.lineTo(sx, sy + 1);
                ctx.lineTo(sx + 3, sy - 2);
                ctx.lineTo(sx + 6, sy);
                ctx.stroke();
                break;
            }
            case 'dust_devil': {
                const dd = phase % (Math.PI * 4);
                const ddx = Math.sin(dd * 2) * 6;
                const ddy = Math.cos(dd * 3) * 4;
                ctx.fillStyle = `rgba(180,150,100,${0.2 + Math.sin(phase * 2) * 0.1})`;
                ctx.beginPath(); ctx.arc(sx + ddx, sy - 8 + ddy, 3, 0, Math.PI * 2); ctx.fill();
                ctx.beginPath(); ctx.arc(sx + ddx * 0.5, sy - 4 + ddy * 0.5, 2, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'crystal_sparkle': {
                const cs2 = Math.sin(phase * 3) * 0.5 + 0.5;
                if (cs2 > 0.4) {
                    ctx.fillStyle = `rgba(180,200,255,${cs2})`;
                    ctx.fillRect(sx, sy - 5, 1, 3);
                    ctx.fillRect(sx - 1, sy - 4, 3, 1);
                }
                break;
            }
            case 'deep_bubble': {
                const db = Math.sin(phase * 2);
                if (db > 0) {
                    const dbY = sy - 5 - db * 10;
                    ctx.fillStyle = `rgba(100,180,200,${(1 - db) * 0.4})`;
                    ctx.beginPath(); ctx.arc(sx, dbY, 2, 0, Math.PI * 2); ctx.fill();
                }
                break;
            }
            case 'vine_drip': {
                const vd = phase % 3;
                if (vd < 1) {
                    const dropY = sy + vd * 10;
                    ctx.fillStyle = `rgba(100,180,80,${(1 - vd) * 0.5})`;
                    ctx.fillRect(sx, dropY, 2, 2);
                }
                break;
            }
            case 'ancient_glyph': {
                const ag = Math.sin(phase) * 0.2 + 0.3;
                ctx.strokeStyle = `rgba(150,180,100,${ag})`;
                ctx.lineWidth = 1;
                ctx.strokeRect(sx - 3, sy - 5, 6, 6);
                ctx.beginPath(); ctx.moveTo(sx - 1, sy - 3); ctx.lineTo(sx + 1, sy - 1); ctx.stroke();
                break;
            }
            case 'blood_puddle': {
                ctx.fillStyle = 'rgba(120,20,20,0.4)';
                ctx.beginPath(); ctx.ellipse(sx, sy, 5, 2, 0, 0, Math.PI * 2); ctx.fill();
                const bp2 = Math.sin(phase * 1.5) * 0.15 + 0.2;
                ctx.fillStyle = `rgba(180,30,30,${bp2})`;
                ctx.beginPath(); ctx.ellipse(sx, sy, 3, 1.5, 0, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'eye_stalk': {
                const es = Math.sin(phase * 1.5);
                ctx.strokeStyle = 'rgba(140,50,50,0.5)';
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(sx, sy); ctx.quadraticCurveTo(sx + es * 3, sy - 5, sx + es, sy - 10); ctx.stroke();
                const eo = Math.sin(phase * 1.2) * 0.4 + 0.6;
                ctx.fillStyle = `rgba(255,80,80,${eo})`;
                ctx.beginPath(); ctx.arc(sx + es, sy - 10, 2.5, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = `rgba(40,0,0,${eo})`;
                ctx.fillRect(sx + es - 0.5, sy - 10.5, 1, 1);
                break;
            }
            case 'shimmer_mote': {
                const sm = Math.sin(phase * 2) * 0.4 + 0.6;
                const smX = sx + Math.sin(phase) * 4;
                const smY = sy - 6 + Math.sin(phase * 1.3) * 3;
                ctx.fillStyle = `rgba(180,230,255,${sm * 0.5})`;
                ctx.beginPath(); ctx.arc(smX, smY, 2, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = `rgba(200,240,255,${sm * 0.2})`;
                ctx.beginPath(); ctx.arc(smX, smY, 5, 0, Math.PI * 2); ctx.fill();
                break;
            }
            case 'crystal_flower': {
                const cf = Math.sin(phase) * 0.3 + 0.7;
                ctx.fillStyle = `rgba(180,220,255,${cf * 0.5})`;
                for (let i = 0; i < 4; i++) {
                    const a = (i / 4) * Math.PI * 2 + phase * 0.2;
                    ctx.fillRect(sx + Math.cos(a) * 3 - 1, sy - 4 + Math.sin(a) * 3, 2, 2);
                }
                ctx.fillStyle = `rgba(220,240,255,${cf * 0.7})`;
                ctx.fillRect(sx - 1, sy - 5, 2, 2);
                break;
            }
            case 'fossil_fragment': {
                ctx.fillStyle = 'rgba(170,160,140,0.5)';
                ctx.fillRect(sx - 3, sy - 1, 6, 2);
                ctx.fillStyle = 'rgba(190,180,160,0.4)';
                ctx.fillRect(sx - 1, sy - 3, 3, 2);
                ctx.fillRect(sx + 1, sy + 1, 2, 1);
                break;
            }
            case 'time_crack_deco': {
                const tc = Math.sin(phase * 2) * 0.3 + 0.5;
                ctx.strokeStyle = `rgba(220,180,80,${tc})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(sx - 3, sy - 2);
                ctx.lineTo(sx, sy + 1);
                ctx.lineTo(sx + 4, sy - 3);
                ctx.stroke();
                ctx.fillStyle = `rgba(255,220,100,${tc * 0.3})`;
                ctx.fillRect(sx - 3, sy - 3, 7, 5);
                break;
            }
            case 'petrified_root': {
                ctx.fillStyle = 'rgba(130,120,100,0.5)';
                ctx.beginPath();
                ctx.moveTo(sx - 4, sy);
                ctx.quadraticCurveTo(sx - 2, sy - 4, sx + 1, sy - 3);
                ctx.quadraticCurveTo(sx + 3, sy - 5, sx + 5, sy - 2);
                ctx.lineWidth = 2;
                ctx.strokeStyle = 'rgba(130,120,100,0.5)';
                ctx.stroke();
                break;
            }
            case 'wind_streak': {
                const ws = Math.sin(phase * 3) * 0.3 + 0.3;
                ctx.fillStyle = `rgba(200,220,240,${ws})`;
                ctx.fillRect(sx - 8, sy - 4, 16, 1);
                ctx.fillRect(sx - 5, sy - 6, 10, 1);
                break;
            }
            // === TIDAL FLATS NEW DECORATIONS ===
            case 'seaweed_tuft': {
                const sway4 = Math.sin(phase * 0.7) * 2;
                ctx.fillStyle = 'rgba(40,120,80,0.5)';
                ctx.fillRect(sx - 1, sy, 2, -6);
                ctx.fillRect(sx - 1 + sway4, sy - 6, 2, -3);
                ctx.fillStyle = 'rgba(50,140,90,0.4)';
                ctx.fillRect(sx + 2, sy, 2, -5);
                ctx.fillRect(sx + 2 + sway4 * 0.7, sy - 5, 2, -2);
                break;
            }
            case 'driftwood_piece': {
                ctx.fillStyle = 'rgba(140,120,80,0.5)';
                ctx.fillRect(sx - 6, sy - 1, 12, 3);
                ctx.fillStyle = 'rgba(120,100,65,0.4)';
                ctx.fillRect(sx - 5, sy - 2, 3, 1);
                ctx.fillRect(sx + 3, sy + 2, 4, 1);
                break;
            }
            case 'tide_foam': {
                const foam = Math.sin(phase * 1.5) * 0.3 + 0.4;
                ctx.fillStyle = `rgba(220,240,250,${foam})`;
                ctx.fillRect(sx - 5, sy, 3, 1);
                ctx.fillRect(sx - 1, sy + 1, 4, 1);
                ctx.fillRect(sx + 4, sy, 2, 1);
                break;
            }
            // === STORM PEAKS NEW DECORATIONS ===
            case 'storm_crack': {
                const sc = Math.sin(phase * 4);
                if (sc > 0.6) {
                    ctx.fillStyle = `rgba(180,220,255,${(sc - 0.6) * 2})`;
                    ctx.fillRect(sx - 1, sy - 8, 2, 4);
                    ctx.fillRect(sx - 3, sy - 5, 2, 3);
                    ctx.fillRect(sx + 1, sy - 3, 2, 3);
                }
                break;
            }
            case 'charged_rock': {
                ctx.fillStyle = 'rgba(80,80,100,0.5)';
                ctx.fillRect(sx - 4, sy - 2, 8, 4);
                ctx.fillStyle = 'rgba(60,70,90,0.4)';
                ctx.fillRect(sx - 3, sy - 3, 6, 1);
                // Occasional spark on rock
                const spark = Math.sin(phase * 5 + d.offset);
                if (spark > 0.85) {
                    ctx.fillStyle = `rgba(180,220,255,${(spark - 0.85) * 6})`;
                    ctx.fillRect(sx - 1, sy - 4, 2, 2);
                }
                break;
            }
            case 'wind_vortex': {
                const vort = phase * 3;
                const va = Math.sin(vort) * 0.3 + 0.2;
                ctx.fillStyle = `rgba(180,200,220,${va})`;
                const r1x = Math.sin(vort) * 4, r1y = Math.cos(vort) * 2;
                const r2x = Math.sin(vort + 2) * 5, r2y = Math.cos(vort + 2) * 3;
                ctx.fillRect(sx + r1x - 1, sy - 5 + r1y, 2, 1);
                ctx.fillRect(sx + r2x - 1, sy - 7 + r2y, 2, 1);
                ctx.fillRect(sx - r1x, sy - 3 - r1y, 2, 1);
                break;
            }
            // === LIVING CAVES NEW DECORATIONS ===
            case 'nerve_cluster': {
                const nc = Math.sin(phase * 1.5) * 0.3 + 0.6;
                ctx.fillStyle = `rgba(200,70,90,${nc * 0.5})`;
                ctx.fillRect(sx - 3, sy - 1, 6, 3);
                // Branching nerves
                ctx.fillStyle = `rgba(220,90,100,${nc * 0.4})`;
                ctx.fillRect(sx - 5, sy - 2, 2, 1);
                ctx.fillRect(sx + 3, sy - 2, 2, 1);
                ctx.fillRect(sx - 4, sy + 2, 2, 1);
                ctx.fillRect(sx + 2, sy + 2, 3, 1);
                break;
            }
            case 'pulsing_eye': {
                const eyeP = Math.sin(phase * 1.2);
                const eyeOpen2 = Math.max(0, eyeP * 0.5 + 0.5);
                if (eyeOpen2 > 0.2) {
                    ctx.fillStyle = `rgba(200,50,60,${eyeOpen2 * 0.6})`;
                    ctx.fillRect(sx - 3, sy - 3, 6, Math.max(1, 4 * eyeOpen2) | 0);
                    // Pupil
                    ctx.fillStyle = `rgba(30,0,0,${eyeOpen2 * 0.8})`;
                    ctx.fillRect(sx - 1, sy - 2, 2, Math.max(1, 2 * eyeOpen2) | 0);
                }
                break;
            }
            case 'organic_tendril': {
                const ot = Math.sin(phase * 0.8) * 3;
                ctx.fillStyle = 'rgba(160,50,60,0.5)';
                ctx.fillRect(sx - 1, sy, 2, -8);
                ctx.fillRect(sx - 1 + ot * 0.5, sy - 8, 2, -3);
                // Tip glow
                const tipG = Math.sin(phase * 2) * 0.3 + 0.4;
                ctx.fillStyle = `rgba(255,100,120,${tipG})`;
                ctx.fillRect(sx - 1 + ot * 0.5, sy - 11, 2, 2);
                break;
            }
        }
    }
}